import React, {
  createContext as createContextReact,
  ReactNode,
  useContext as useReactContext,
  useMemo,
  useCallback
} from 'react';
import { useIntl } from 'react-intl';

import {
  AccountCategory,
  AccountType,
  Metrics,
  NodeStaticId
} from '../../../predict/src/forecast/prediction-builder/types';

export interface LocalisedConstantsContextProps {
  countriesList: string[];
  accountCategoryNames: Record<AccountCategory, string>;
  accountStaticIdOverrides: Record<string, string>;
  nodeStaticIds: Record<NodeStaticId, string>;
  getCategorizeError422Message: (
    errorMessage: string,
    originalCategory: string,
    newCategory: string
  ) => string | undefined;
}

interface Props {
  children: ReactNode;
}

function createContext<ContextType>() {
  const context = createContextReact<ContextType | undefined>(undefined);
  function useContext() {
    const contextValue = useReactContext(context);

    if (!contextValue) {
      throw new Error('useContext must be inside a Provider with a value');
    }
    return contextValue;
  }
  return { Provider: context.Provider, useContext } as const;
}

export const LocalisedConstantsContext = createContext<LocalisedConstantsContextProps>();

export const LocalisedConstantsContextProvider = ({ children }: Props) => {
  const intl = useIntl();

  const countriesList = useMemo(
    () => [
      intl.formatMessage({
        id: 'country.afghanistan',
        defaultMessage: 'Afghanistan'
      }),
      intl.formatMessage({
        id: 'country.aland_islands',
        defaultMessage: 'Aland Islands'
      }),
      intl.formatMessage({
        id: 'country.albania',
        defaultMessage: 'Albania'
      }),
      intl.formatMessage({
        id: 'country.algeria',
        defaultMessage: 'Algeria'
      }),
      intl.formatMessage({
        id: 'country.andorra',
        defaultMessage: 'Andorra'
      }),
      intl.formatMessage({
        id: 'country.angola',
        defaultMessage: 'Angola'
      }),
      intl.formatMessage({
        id: 'country.anguilla',
        defaultMessage: 'Anguilla'
      }),
      intl.formatMessage({
        id: 'country.antarctica',
        defaultMessage: 'Antarctica'
      }),
      intl.formatMessage({
        id: 'country.antigua_and_barbuda',
        defaultMessage: 'Antigua and Barbuda'
      }),
      intl.formatMessage({
        id: 'country.argentina',
        defaultMessage: 'Argentina'
      }),
      intl.formatMessage({
        id: 'country.armenia',
        defaultMessage: 'Armenia'
      }),
      intl.formatMessage({
        id: 'country.aruba',
        defaultMessage: 'Aruba'
      }),
      intl.formatMessage({
        id: 'country.australia',
        defaultMessage: 'Australia'
      }),
      intl.formatMessage({
        id: 'country.austria',
        defaultMessage: 'Austria'
      }),
      intl.formatMessage({
        id: 'country.azerbaijan',
        defaultMessage: 'Azerbaijan'
      }),
      intl.formatMessage({
        id: 'country.bahamas',
        defaultMessage: 'Bahamas'
      }),
      intl.formatMessage({
        id: 'country.bahrain',
        defaultMessage: 'Bahrain'
      }),
      intl.formatMessage({
        id: 'country.bangladesh',
        defaultMessage: 'Bangladesh'
      }),
      intl.formatMessage({
        id: 'country.barbados',
        defaultMessage: 'Barbados'
      }),
      intl.formatMessage({
        id: 'country.belarus',
        defaultMessage: 'Belarus'
      }),
      intl.formatMessage({
        id: 'country.belgium',
        defaultMessage: 'Belgium'
      }),
      intl.formatMessage({
        id: 'country.benin',
        defaultMessage: 'Benin'
      }),
      intl.formatMessage({
        id: 'country.bermuda',
        defaultMessage: 'Bermuda'
      }),
      intl.formatMessage({
        id: 'country.bhutan',
        defaultMessage: 'Bhutan'
      }),
      intl.formatMessage({
        id: 'country.bolivia_plurinational_state_of',
        defaultMessage: 'Bolivia, Plurinational State of'
      }),
      intl.formatMessage({
        id: 'country.bonaire_sint_eustatius_and_saba',
        defaultMessage: 'Bonaire, Sint Eustatius and Saba'
      }),
      intl.formatMessage({
        id: 'country.bosnia_and_herzegovina',
        defaultMessage: 'Bosnia and Herzegovina'
      }),
      intl.formatMessage({
        id: 'country.botswana',
        defaultMessage: 'Botswana'
      }),
      intl.formatMessage({
        id: 'country.bouvet_island',
        defaultMessage: 'Bouvet Island'
      }),
      intl.formatMessage({
        id: 'country.brazil',
        defaultMessage: 'Brazil'
      }),
      intl.formatMessage({
        id: 'country.british_indian_ocean_territory',
        defaultMessage: 'British Indian Ocean Territory'
      }),
      intl.formatMessage({
        id: 'country.brunei_darussalam',
        defaultMessage: 'Brunei Darussalam'
      }),
      intl.formatMessage({
        id: 'country.bulgaria',
        defaultMessage: 'Bulgaria'
      }),
      intl.formatMessage({
        id: 'country.burkina_faso',
        defaultMessage: 'Burkina Faso'
      }),
      intl.formatMessage({
        id: 'country.belize',
        defaultMessage: 'Belize'
      }),
      intl.formatMessage({
        id: 'country.burundi',
        defaultMessage: 'Burundi'
      }),
      intl.formatMessage({
        id: 'country.cambodia',
        defaultMessage: 'Cambodia'
      }),
      intl.formatMessage({
        id: 'country.cameroon',
        defaultMessage: 'Cameroon'
      }),
      intl.formatMessage({
        id: 'country.canada',
        defaultMessage: 'Canada'
      }),
      intl.formatMessage({
        id: 'country.cape_verde',
        defaultMessage: 'Cape Verde'
      }),
      intl.formatMessage({
        id: 'country.cayman_islands',
        defaultMessage: 'Cayman Islands'
      }),
      intl.formatMessage({
        id: 'country.central_african_republic',
        defaultMessage: 'Central African Republic'
      }),
      intl.formatMessage({
        id: 'country.chad',
        defaultMessage: 'Chad'
      }),
      intl.formatMessage({
        id: 'country.chile',
        defaultMessage: 'Chile'
      }),
      intl.formatMessage({
        id: 'country.china',
        defaultMessage: 'China'
      }),
      intl.formatMessage({
        id: 'country.christmas_island',
        defaultMessage: 'Christmas Island'
      }),
      intl.formatMessage({
        id: 'country.cocos_keeling_islands',
        defaultMessage: 'Cocos (Keeling) Islands'
      }),
      intl.formatMessage({
        id: 'country.colombia',
        defaultMessage: 'Colombia'
      }),
      intl.formatMessage({
        id: 'country.comoros',
        defaultMessage: 'Comoros'
      }),
      intl.formatMessage({
        id: 'country.congo',
        defaultMessage: 'Congo'
      }),
      intl.formatMessage({
        id: 'country.congo_the_democratic_republic_of_the',
        defaultMessage: 'Congo, the Democratic Republic of the'
      }),
      intl.formatMessage({
        id: 'country.cook_islands',
        defaultMessage: 'Cook Islands'
      }),
      intl.formatMessage({
        id: 'country.costa_rica',
        defaultMessage: 'Costa Rica'
      }),
      intl.formatMessage({
        id: 'country.cote_divoire',
        defaultMessage: "Cote d'Ivoire"
      }),
      intl.formatMessage({
        id: 'country.croatia',
        defaultMessage: 'Croatia'
      }),
      intl.formatMessage({
        id: 'country.cuba',
        defaultMessage: 'Cuba'
      }),
      intl.formatMessage({
        id: 'country.curacao',
        defaultMessage: 'Curacao'
      }),
      intl.formatMessage({
        id: 'country.cyprus',
        defaultMessage: 'Cyprus'
      }),
      intl.formatMessage({
        id: 'country.czech_republic',
        defaultMessage: 'Czech Republic'
      }),
      intl.formatMessage({
        id: 'country.denmark',
        defaultMessage: 'Denmark'
      }),
      intl.formatMessage({
        id: 'country.djibouti',
        defaultMessage: 'Djibouti'
      }),
      intl.formatMessage({
        id: 'country.dominica',
        defaultMessage: 'Dominica'
      }),
      intl.formatMessage({
        id: 'country.dominican_republic',
        defaultMessage: 'Dominican Republic'
      }),
      intl.formatMessage({
        id: 'country.ecuador',
        defaultMessage: 'Ecuador'
      }),
      intl.formatMessage({
        id: 'country.egypt',
        defaultMessage: 'Egypt'
      }),
      intl.formatMessage({
        id: 'country.el_salvador',
        defaultMessage: 'El Salvador'
      }),
      intl.formatMessage({
        id: 'country.equatorial_guinea',
        defaultMessage: 'Equatorial Guinea'
      }),
      intl.formatMessage({
        id: 'country.eritrea',
        defaultMessage: 'Eritrea'
      }),
      intl.formatMessage({
        id: 'country.estonia',
        defaultMessage: 'Estonia'
      }),
      intl.formatMessage({
        id: 'country.ethiopia',
        defaultMessage: 'Ethiopia'
      }),
      intl.formatMessage({
        id: 'country.falkland_islands_malvinas',
        defaultMessage: 'Falkland Islands (Malvinas)'
      }),
      intl.formatMessage({
        id: 'country.faroe_islands',
        defaultMessage: 'Faroe Islands'
      }),
      intl.formatMessage({
        id: 'country.fiji',
        defaultMessage: 'Fiji'
      }),
      intl.formatMessage({
        id: 'country.finland',
        defaultMessage: 'Finland'
      }),
      intl.formatMessage({
        id: 'country.france',
        defaultMessage: 'France'
      }),
      intl.formatMessage({
        id: 'country.french_guiana',
        defaultMessage: 'French Guiana'
      }),
      intl.formatMessage({
        id: 'country.french_polynesia',
        defaultMessage: 'French Polynesia'
      }),
      intl.formatMessage({
        id: 'country.french_southern_territories',
        defaultMessage: 'French Southern Territories'
      }),
      intl.formatMessage({
        id: 'country.gabon',
        defaultMessage: 'Gabon'
      }),
      intl.formatMessage({
        id: 'country.gambia',
        defaultMessage: 'Gambia'
      }),
      intl.formatMessage({
        id: 'country.georgia',
        defaultMessage: 'Georgia'
      }),
      intl.formatMessage({
        id: 'country.germany',
        defaultMessage: 'Germany'
      }),
      intl.formatMessage({
        id: 'country.ghana',
        defaultMessage: 'Ghana'
      }),
      intl.formatMessage({
        id: 'country.gibraltar',
        defaultMessage: 'Gibraltar'
      }),
      intl.formatMessage({
        id: 'country.greece',
        defaultMessage: 'Greece'
      }),
      intl.formatMessage({
        id: 'country.greenland',
        defaultMessage: 'Greenland'
      }),
      intl.formatMessage({
        id: 'country.grenada',
        defaultMessage: 'Grenada'
      }),
      intl.formatMessage({
        id: 'country.guadeloupe',
        defaultMessage: 'Guadeloupe'
      }),
      intl.formatMessage({
        id: 'country.guatemala',
        defaultMessage: 'Guatemala'
      }),
      intl.formatMessage({
        id: 'country.guernsey',
        defaultMessage: 'Guernsey'
      }),
      intl.formatMessage({
        id: 'country.guinea',
        defaultMessage: 'Guinea'
      }),
      intl.formatMessage({
        id: 'country.guinea-bissau',
        defaultMessage: 'Guinea-Bissau'
      }),
      intl.formatMessage({
        id: 'country.guyana',
        defaultMessage: 'Guyana'
      }),
      intl.formatMessage({
        id: 'country.haiti',
        defaultMessage: 'Haiti'
      }),
      intl.formatMessage({
        id: 'country.heard_island_and_mcdonald_islands',
        defaultMessage: 'Heard Island and McDonald Islands'
      }),
      intl.formatMessage({
        id: 'country.holy_see_vatican_city_state',
        defaultMessage: 'Holy See (Vatican City State)'
      }),
      intl.formatMessage({
        id: 'country.honduras',
        defaultMessage: 'Honduras'
      }),
      intl.formatMessage({
        id: 'country.hungary',
        defaultMessage: 'Hungary'
      }),
      intl.formatMessage({
        id: 'country.iceland',
        defaultMessage: 'Iceland'
      }),
      intl.formatMessage({
        id: 'country.india',
        defaultMessage: 'India'
      }),
      intl.formatMessage({
        id: 'country.indonesia',
        defaultMessage: 'Indonesia'
      }),
      intl.formatMessage({
        id: 'country.iran_islamic_republic_of',
        defaultMessage: 'Iran, Islamic Republic of'
      }),
      intl.formatMessage({
        id: 'country.iraq',
        defaultMessage: 'Iraq'
      }),
      intl.formatMessage({
        id: 'country.ireland',
        defaultMessage: 'Ireland'
      }),
      intl.formatMessage({
        id: 'country.isle_of_man',
        defaultMessage: 'Isle of Man'
      }),
      intl.formatMessage({
        id: 'country.israel',
        defaultMessage: 'Israel'
      }),
      intl.formatMessage({
        id: 'country.italy',
        defaultMessage: 'Italy'
      }),
      intl.formatMessage({
        id: 'country.jamaica',
        defaultMessage: 'Jamaica'
      }),
      intl.formatMessage({
        id: 'country.japan',
        defaultMessage: 'Japan'
      }),
      intl.formatMessage({
        id: 'country.jersey',
        defaultMessage: 'Jersey'
      }),
      intl.formatMessage({
        id: 'country.jordan',
        defaultMessage: 'Jordan'
      }),
      intl.formatMessage({
        id: 'country.kazakhstan',
        defaultMessage: 'Kazakhstan'
      }),
      intl.formatMessage({
        id: 'country.kenya',
        defaultMessage: 'Kenya'
      }),
      intl.formatMessage({
        id: 'country.kiribati',
        defaultMessage: 'Kiribati'
      }),
      intl.formatMessage({
        id: 'country.korea_democratic_peoples_republic_of',
        defaultMessage: "Korea, Democratic People's Republic of"
      }),
      intl.formatMessage({
        id: 'country.korea_republic_of',
        defaultMessage: 'Korea, Republic of'
      }),
      intl.formatMessage({
        id: 'country.kuwait',
        defaultMessage: 'Kuwait'
      }),
      intl.formatMessage({
        id: 'country.kyrgyzstan',
        defaultMessage: 'Kyrgyzstan'
      }),
      intl.formatMessage({
        id: 'country.lao_peoples_democratic_republic',
        defaultMessage: "Lao People's Democratic Republic"
      }),
      intl.formatMessage({
        id: 'country.latvia',
        defaultMessage: 'Latvia'
      }),
      intl.formatMessage({
        id: 'country.lebanon',
        defaultMessage: 'Lebanon'
      }),
      intl.formatMessage({
        id: 'country.lesotho',
        defaultMessage: 'Lesotho'
      }),
      intl.formatMessage({
        id: 'country.liberia',
        defaultMessage: 'Liberia'
      }),
      intl.formatMessage({
        id: 'country.libya',
        defaultMessage: 'Libya'
      }),
      intl.formatMessage({
        id: 'country.liechtenstein',
        defaultMessage: 'Liechtenstein'
      }),
      intl.formatMessage({
        id: 'country.lithuania',
        defaultMessage: 'Lithuania'
      }),
      intl.formatMessage({
        id: 'country.luxembourg',
        defaultMessage: 'Luxembourg'
      }),
      intl.formatMessage({
        id: 'country.macao',
        defaultMessage: 'Macao'
      }),
      intl.formatMessage({
        id: 'country.macedonia_the_former_yugoslav_republic_of',
        defaultMessage: 'Macedonia, the former Yugoslav Republic of'
      }),
      intl.formatMessage({
        id: 'country.madagascar',
        defaultMessage: 'Madagascar'
      }),
      intl.formatMessage({
        id: 'country.malawi',
        defaultMessage: 'Malawi'
      }),
      intl.formatMessage({
        id: 'country.malaysia',
        defaultMessage: 'Malaysia'
      }),
      intl.formatMessage({
        id: 'country.maldives',
        defaultMessage: 'Maldives'
      }),
      intl.formatMessage({
        id: 'country.mali',
        defaultMessage: 'Mali'
      }),
      intl.formatMessage({
        id: 'country.malta',
        defaultMessage: 'Malta'
      }),
      intl.formatMessage({
        id: 'country.martinique',
        defaultMessage: 'Martinique'
      }),
      intl.formatMessage({
        id: 'country.mauritania',
        defaultMessage: 'Mauritania'
      }),
      intl.formatMessage({
        id: 'country.mauritius',
        defaultMessage: 'Mauritius'
      }),
      intl.formatMessage({
        id: 'country.mayotte',
        defaultMessage: 'Mayotte'
      }),
      intl.formatMessage({
        id: 'country.mexico',
        defaultMessage: 'Mexico'
      }),
      intl.formatMessage({
        id: 'country.moldova_republic_of',
        defaultMessage: 'Moldova, Republic of'
      }),
      intl.formatMessage({
        id: 'country.monaco',
        defaultMessage: 'Monaco'
      }),
      intl.formatMessage({
        id: 'country.mongolia',
        defaultMessage: 'Mongolia'
      }),
      intl.formatMessage({
        id: 'country.montenegro',
        defaultMessage: 'Montenegro'
      }),
      intl.formatMessage({
        id: 'country.montserrat',
        defaultMessage: 'Montserrat'
      }),
      intl.formatMessage({
        id: 'country.morocco',
        defaultMessage: 'Morocco'
      }),
      intl.formatMessage({
        id: 'country.mozambique',
        defaultMessage: 'Mozambique'
      }),
      intl.formatMessage({
        id: 'country.myanmar',
        defaultMessage: 'Myanmar'
      }),
      intl.formatMessage({
        id: 'country.namibia',
        defaultMessage: 'Namibia'
      }),
      intl.formatMessage({
        id: 'country.nauru',
        defaultMessage: 'Nauru'
      }),
      intl.formatMessage({
        id: 'country.nepal',
        defaultMessage: 'Nepal'
      }),
      intl.formatMessage({
        id: 'country.netherlands',
        defaultMessage: 'Netherlands'
      }),
      intl.formatMessage({
        id: 'country.new_caledonia',
        defaultMessage: 'New Caledonia'
      }),
      intl.formatMessage({
        id: 'country.new_zealand',
        defaultMessage: 'New Zealand'
      }),
      intl.formatMessage({
        id: 'country.nicaragua',
        defaultMessage: 'Nicaragua'
      }),
      intl.formatMessage({
        id: 'country.niger',
        defaultMessage: 'Niger'
      }),
      intl.formatMessage({
        id: 'country.nigeria',
        defaultMessage: 'Nigeria'
      }),
      intl.formatMessage({
        id: 'country.niue',
        defaultMessage: 'Niue'
      }),
      intl.formatMessage({
        id: 'country.norfolk_island',
        defaultMessage: 'Norfolk Island'
      }),
      intl.formatMessage({
        id: 'country.norway',
        defaultMessage: 'Norway'
      }),
      intl.formatMessage({
        id: 'country.oman',
        defaultMessage: 'Oman'
      }),
      intl.formatMessage({
        id: 'country.pakistan',
        defaultMessage: 'Pakistan'
      }),
      intl.formatMessage({
        id: 'country.palestine',
        defaultMessage: 'Palestine'
      }),
      intl.formatMessage({
        id: 'country.panama',
        defaultMessage: 'Panama'
      }),
      intl.formatMessage({
        id: 'country.papua_new_guinea',
        defaultMessage: 'Papua New Guinea'
      }),
      intl.formatMessage({
        id: 'country.paraguay',
        defaultMessage: 'Paraguay'
      }),
      intl.formatMessage({
        id: 'country.peru',
        defaultMessage: 'Peru'
      }),
      intl.formatMessage({
        id: 'country.philippines',
        defaultMessage: 'Philippines'
      }),
      intl.formatMessage({
        id: 'country.pitcairn',
        defaultMessage: 'Pitcairn'
      }),
      intl.formatMessage({
        id: 'country.poland',
        defaultMessage: 'Poland'
      }),
      intl.formatMessage({
        id: 'country.portugal',
        defaultMessage: 'Portugal'
      }),
      intl.formatMessage({
        id: 'country.qatar',
        defaultMessage: 'Qatar'
      }),
      intl.formatMessage({
        id: 'country.reunion',
        defaultMessage: 'Reunion'
      }),
      intl.formatMessage({
        id: 'country.romania',
        defaultMessage: 'Romania'
      }),
      intl.formatMessage({
        id: 'country.russian_federation',
        defaultMessage: 'Russian Federation'
      }),
      intl.formatMessage({
        id: 'country.rwanda',
        defaultMessage: 'Rwanda'
      }),
      intl.formatMessage({
        id: 'country.saint_barthelemy',
        defaultMessage: 'Saint Barthelemy'
      }),
      intl.formatMessage({
        id: 'country.saint_helena_ascension_and_tristan_da_cunha',
        defaultMessage: 'Saint Helena, Ascension and Tristan da Cunha'
      }),
      intl.formatMessage({
        id: 'country.saint_kitts_and_nevis',
        defaultMessage: 'Saint Kitts and Nevis'
      }),
      intl.formatMessage({
        id: 'country.saint_lucia',
        defaultMessage: 'Saint Lucia'
      }),
      intl.formatMessage({
        id: 'country.saint_martin_french_part',
        defaultMessage: 'Saint Martin (French part)'
      }),
      intl.formatMessage({
        id: 'country.saint_pierre_and_miquelon',
        defaultMessage: 'Saint Pierre and Miquelon'
      }),
      intl.formatMessage({
        id: 'country.saint_vincent_and_the_grenadines',
        defaultMessage: 'Saint Vincent and the Grenadines'
      }),
      intl.formatMessage({
        id: 'country.samoa',
        defaultMessage: 'Samoa'
      }),
      intl.formatMessage({
        id: 'country.san_marino',
        defaultMessage: 'San Marino'
      }),
      intl.formatMessage({
        id: 'country.sao_tome_and_principe',
        defaultMessage: 'Sao Tome and Principe'
      }),
      intl.formatMessage({
        id: 'country.saudi_arabia',
        defaultMessage: 'Saudi Arabia'
      }),
      intl.formatMessage({
        id: 'country.senegal',
        defaultMessage: 'Senegal'
      }),
      intl.formatMessage({
        id: 'country.serbia',
        defaultMessage: 'Serbia'
      }),
      intl.formatMessage({
        id: 'country.seychelles',
        defaultMessage: 'Seychelles'
      }),
      intl.formatMessage({
        id: 'country.sierra_leone',
        defaultMessage: 'Sierra Leone'
      }),
      intl.formatMessage({
        id: 'country.singapore',
        defaultMessage: 'Singapore'
      }),
      intl.formatMessage({
        id: 'country.sint_maarten_dutch_part',
        defaultMessage: 'Sint Maarten (Dutch part)'
      }),
      intl.formatMessage({
        id: 'country.slovakia',
        defaultMessage: 'Slovakia'
      }),
      intl.formatMessage({
        id: 'country.slovenia',
        defaultMessage: 'Slovenia'
      }),
      intl.formatMessage({
        id: 'country.solomon_islands',
        defaultMessage: 'Solomon Islands'
      }),
      intl.formatMessage({
        id: 'country.somalia',
        defaultMessage: 'Somalia'
      }),
      intl.formatMessage({
        id: 'country.south_africa',
        defaultMessage: 'South Africa'
      }),
      intl.formatMessage({
        id: 'country.south_georgia_and_the_south_sandwich_islands',
        defaultMessage: 'South Georgia and the South Sandwich Islands'
      }),
      intl.formatMessage({
        id: 'country.south_sudan',
        defaultMessage: 'South Sudan'
      }),
      intl.formatMessage({
        id: 'country.spain',
        defaultMessage: 'Spain'
      }),
      intl.formatMessage({
        id: 'country.sri_lanka',
        defaultMessage: 'Sri Lanka'
      }),
      intl.formatMessage({
        id: 'country.sudan',
        defaultMessage: 'Sudan'
      }),
      intl.formatMessage({
        id: 'country.suriname',
        defaultMessage: 'Suriname'
      }),
      intl.formatMessage({
        id: 'country.svalbard_and_jan_mayen',
        defaultMessage: 'Svalbard and Jan Mayen'
      }),
      intl.formatMessage({
        id: 'country.swaziland',
        defaultMessage: 'Swaziland'
      }),
      intl.formatMessage({
        id: 'country.sweden',
        defaultMessage: 'Sweden'
      }),
      intl.formatMessage({
        id: 'country.switzerland',
        defaultMessage: 'Switzerland'
      }),
      intl.formatMessage({
        id: 'country.syrian_arab_republic',
        defaultMessage: 'Syrian Arab Republic'
      }),
      intl.formatMessage({
        id: 'country.taiwan',
        defaultMessage: 'Taiwan'
      }),
      intl.formatMessage({
        id: 'country.tajikistan',
        defaultMessage: 'Tajikistan'
      }),
      intl.formatMessage({
        id: 'country.tanzania_united_republic_of',
        defaultMessage: 'Tanzania, United Republic of'
      }),
      intl.formatMessage({
        id: 'country.thailand',
        defaultMessage: 'Thailand'
      }),
      intl.formatMessage({
        id: 'country.timor-leste',
        defaultMessage: 'Timor-Leste'
      }),
      intl.formatMessage({
        id: 'country.togo',
        defaultMessage: 'Togo'
      }),
      intl.formatMessage({
        id: 'country.tokelau',
        defaultMessage: 'Tokelau'
      }),
      intl.formatMessage({
        id: 'country.tonga',
        defaultMessage: 'Tonga'
      }),
      intl.formatMessage({
        id: 'country.trinidad_and_tobago',
        defaultMessage: 'Trinidad and Tobago'
      }),
      intl.formatMessage({
        id: 'country.tunisia',
        defaultMessage: 'Tunisia'
      }),
      intl.formatMessage({
        id: 'country.turkey',
        defaultMessage: 'Turkey'
      }),
      intl.formatMessage({
        id: 'country.turkmenistan',
        defaultMessage: 'Turkmenistan'
      }),
      intl.formatMessage({
        id: 'country.turks_and_caicos_islands',
        defaultMessage: 'Turks and Caicos Islands'
      }),
      intl.formatMessage({
        id: 'country.tuvalu',
        defaultMessage: 'Tuvalu'
      }),
      intl.formatMessage({
        id: 'country.uganda',
        defaultMessage: 'Uganda'
      }),
      intl.formatMessage({
        id: 'country.ukraine',
        defaultMessage: 'Ukraine'
      }),
      intl.formatMessage({
        id: 'country.united_arab_emirates',
        defaultMessage: 'United Arab Emirates'
      }),
      intl.formatMessage({
        id: 'country.united_kingdom',
        defaultMessage: 'United Kingdom'
      }),
      intl.formatMessage({
        id: 'country.united_states',
        defaultMessage: 'United States'
      }),
      intl.formatMessage({
        id: 'country.uruguay',
        defaultMessage: 'Uruguay'
      }),
      intl.formatMessage({
        id: 'country.uzbekistan',
        defaultMessage: 'Uzbekistan'
      }),
      intl.formatMessage({
        id: 'country.vanuatu',
        defaultMessage: 'Vanuatu'
      }),
      intl.formatMessage({
        id: 'country.venezuela_bolivarian_republic_of',
        defaultMessage: 'Venezuela, Bolivarian Republic of'
      }),
      intl.formatMessage({
        id: 'country.viet_nam',
        defaultMessage: 'Viet Nam'
      }),
      intl.formatMessage({
        id: 'country.virgin_islands_british',
        defaultMessage: 'Virgin Islands, British'
      }),
      intl.formatMessage({
        id: 'country.wallis_and_futuna',
        defaultMessage: 'Wallis and Futuna'
      }),
      intl.formatMessage({
        id: 'country.western_sahara',
        defaultMessage: 'Western Sahara'
      }),
      intl.formatMessage({
        id: 'country.yemen',
        defaultMessage: 'Yemen'
      }),
      intl.formatMessage({
        id: 'country.zambia',
        defaultMessage: 'Zambia'
      }),
      intl.formatMessage({
        id: 'country.zimbabwe',
        defaultMessage: 'Zimbabwe'
      }),
      intl.formatMessage({
        id: 'country.zimbabwe',
        defaultMessage: 'Zimbabwe'
      })
    ],
    [intl]
  );

  const accountCategoryNames: Record<AccountCategory, string> = useMemo(
    () => ({
      income: intl.formatMessage({ id: 'financial_fields.category.income', defaultMessage: 'Income' }),
      cost_of_sales: intl.formatMessage({
        id: 'financial_fields.category.cost_of_sales',
        defaultMessage: 'Cost of Sales'
      }),
      expenses: intl.formatMessage({ id: 'financial_fields.category.expenses', defaultMessage: 'Expenses' }),
      other_income: intl.formatMessage({
        id: 'financial_fields.category.other_income',
        defaultMessage: 'Other Income'
      }),
      other_expenses: intl.formatMessage({
        id: 'financial_fields.category.other_expenses',
        defaultMessage: 'Other Expenses'
      }),
      bank_accounts: intl.formatMessage({
        id: 'financial_fields.category.bank_accounts',
        defaultMessage: 'Bank Accounts'
      }),
      fixed_assets: intl.formatMessage({
        id: 'financial_fields.category.fixed_assets',
        defaultMessage: 'Fixed Assets'
      }),
      current_assets: intl.formatMessage({
        id: 'financial_fields.category.current_assets',
        defaultMessage: 'Current Assets'
      }),
      non_current_assets: intl.formatMessage({
        id: 'financial_fields.category.non_current_assets',
        defaultMessage: 'Non Current Assets'
      }),
      current_liabilities: intl.formatMessage({
        id: 'financial_fields.category.current_liabilities',
        defaultMessage: 'Current Liabilities'
      }),
      non_current_liabilities: intl.formatMessage({
        id: 'financial_fields.category.non_current_liabilities',
        defaultMessage: 'Non Current Liabilities'
      }),
      equity: intl.formatMessage({ id: 'financial_fields.category.equity', defaultMessage: 'Equity' }),
      cash_flow: intl.formatMessage({ id: 'financial_fields.type.cash_flow', defaultMessage: 'Cash Flow' }),
      balance_sheet: intl.formatMessage({ id: 'financial_fields.type.balance_sheet', defaultMessage: 'Balance Sheet' }),
      profit_and_loss: intl.formatMessage({
        id: 'financial_fields.type.profit_and_loss',
        defaultMessage: 'Profit & Loss'
      })
    }),
    [intl]
  );

  const metrics: Record<Metrics, string> = useMemo(
    () => ({
      'gross_profit': intl.formatMessage({
        id: 'financial_fields.metrics.gross_profit',
        defaultMessage: 'Gross Profit'
      }),
      'gross_profit_%': intl.formatMessage({
        id: 'financial_fields.metrics.gross_profit_%',
        defaultMessage: 'Gross Profit %'
      }),
      'operating_profit': intl.formatMessage({
        id: 'financial_fields.metrics.operating_profit',
        defaultMessage: 'Operating Profit'
      }),
      'operating_profit_%': intl.formatMessage({
        id: 'financial_fields.metrics.operating_profit_%',
        defaultMessage: 'Operating Profit %'
      }),
      'net_profit': intl.formatMessage({
        id: 'financial_fields.metrics.net_profit',
        defaultMessage: 'Net Profit'
      }),
      'net_profit_%': intl.formatMessage({
        id: 'financial_fields.metrics.net_profit_%',
        defaultMessage: 'Net Profit %'
      }),
      'total_assets': intl.formatMessage({
        id: 'financial_fields.metrics.total_assets',
        defaultMessage: 'Total Assets'
      }),
      'total_liabilities': intl.formatMessage({
        id: 'financial_fields.metrics.total_liabilities',
        defaultMessage: 'Total Liabilities'
      }),
      'net_assets': intl.formatMessage({
        id: 'financial_fields.metrics.net_assets',
        defaultMessage: 'Net Assets'
      }),
      'current_year_earnings': intl.formatMessage({
        id: 'financial_fields.metrics.current_year_earnings',
        defaultMessage: 'Current Year Earnings'
      }),
      'currency_translation_difference': intl.formatMessage({
        id: 'financial_fields.metrics.currency_translation_difference',
        defaultMessage: 'Currency Translation Difference'
      }),
      'transfers_to_retained_earnings': intl.formatMessage({
        id: 'financial_fields.metrics.transfers_to_retained_earnings',
        defaultMessage: 'Transfers to Retained Earnings'
      }),
      'cash_from_business_activities': intl.formatMessage({
        id: 'financial_fields.metrics.cash_from_business_activities',
        defaultMessage: 'Cash: Business Activities'
      }),
      'cash_from_financing_and_investing': intl.formatMessage({
        id: 'financial_fields.metrics.cash_from_financing_and_investing',
        defaultMessage: 'Cash: Financing and Investing'
      }),
      'starting_balance': intl.formatMessage({
        id: 'financial_fields.metrics.starting_balance',
        defaultMessage: 'Cash Starting Balance'
      }),
      'cash_in': intl.formatMessage({
        id: 'financial_fields.metrics.cash_in',
        defaultMessage: 'Cash In'
      }),
      'cash_out': intl.formatMessage({
        id: 'financial_fields.metrics.cash_out',
        defaultMessage: 'Cash Out'
      }),
      'net_cash_flow': intl.formatMessage({
        id: 'financial_fields.metrics.net_cash_flow',
        defaultMessage: 'Net Cash Flow'
      }),
      'closing_balance': intl.formatMessage({
        id: 'financial_fields.metrics.closing_balance',
        defaultMessage: 'Cash Closing Balance'
      })
    }),
    [intl]
  );

  const accountTypes: Record<AccountType, string> = useMemo(
    () => ({
      asset: intl.formatMessage({ id: 'financial_fields.account_type.asset', defaultMessage: 'Asset' }),
      liability: intl.formatMessage({ id: 'financial_fields.account_type.liability', defaultMessage: 'Liability' }),
      equity: intl.formatMessage({ id: 'financial_fields.account_type.equity', defaultMessage: 'Equity' }),
      income: intl.formatMessage({ id: 'financial_fields.account_type.income', defaultMessage: 'Income' }),
      expense: intl.formatMessage({ id: 'financial_fields.account_type.expense', defaultMessage: 'Expense' })
    }),
    [intl]
  );

  const accountStaticIdOverrides: Record<string, string> = useMemo(
    () => ({
      costs: intl.formatMessage({ id: 'predict.predictions.costs', defaultMessage: 'Costs' }),
      sales: intl.formatMessage({ id: 'financial_fields.category.sales', defaultMessage: 'Sales' })
    }),
    [intl]
  );

  const nodeStaticIds: Record<NodeStaticId, string> = useMemo(
    () => ({
      ...accountCategoryNames,
      ...metrics,
      ...accountTypes
    }),
    [accountCategoryNames, metrics, accountTypes]
  );

  const getCategorizeError422Message = useCallback(
    (errorMessage: string, originalCategory: string, newCategory: string) => {
      switch (errorMessage) {
        case 'no-such-account':
          return intl.formatMessage({
            id: 'error.missing_account',
            defaultMessage: "The account you attempted to reclassify doesn't exist in the system"
          });
        case 'invalid-category':
          return intl.formatMessage(
            {
              id: 'predict.error.invalid-category',
              defaultMessage: 'Cannot reclassify from {originalCategory} to {newCategory}'
            },
            { originalCategory, newCategory }
          );
        case 'is-default-account':
          return intl.formatMessage({
            id: 'predict.error.is-default-account-contact-support',
            defaultMessage: 'This account cannot be reclassified as it is used as a default system account.\nPlease contact the support team for further assistance.'
          });
        case 'invalidates-switch-rule':
          return intl.formatMessage(
            {
              id: 'predict.error.invalidates-switch-rule',
              defaultMessage:
                'Cannot reclassify from {originalCategory} to {newCategory} as it would invalidate the switch-rule on this account. Remove the switch-rule to continue.'
            },
            { originalCategory, newCategory }
          );
        default:
          return undefined;
      }
    },
    [intl]
  );

  const contextData = useMemo(
    () => ({
      countriesList,
      accountCategoryNames,
      accountStaticIdOverrides,
      nodeStaticIds,
      getCategorizeError422Message
    }),
    [countriesList, accountStaticIdOverrides, accountCategoryNames, nodeStaticIds, getCategorizeError422Message]
  );

  return <LocalisedConstantsContext.Provider value={{ ...contextData }}>{children}</LocalisedConstantsContext.Provider>;
};

export const useLocalisedConstantsContext = LocalisedConstantsContext.useContext;
